/**
 * Shared building blocks for the Duplicates + Collections screens.
 * Everything styles with @vault/ui tokens; no hardcoded theme colors.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Icon, Modal, Toast, BROWSER_COLORS } from '@vault/ui';
import type { ToastTone, BrowserName } from '@vault/ui';
import type { Browser } from '@vault/shared';
import { ApiError } from '../../api/client.js';

// ── error helpers ──────────────────────────────────────────────────────────
export function isApiStatus(err: unknown, status: number): boolean {
  return err instanceof ApiError && err.status === status;
}
export function errMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message || err.code;
  if (err instanceof Error && err.message) return err.message;
  return 'Please try again.';
}

// ── formatting ─────────────────────────────────────────────────────────────
export function hostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0] ?? url;
  }
}

export function initial(source: string): string {
  const cleaned = source.trim().replace(/^https?:\/\//, '').replace(/^www\./, '');
  const ch = cleaned.charAt(0);
  return (ch || '?').toUpperCase();
}

export function formatDate(ms: number): string {
  return new Date(ms)
    .toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    .toUpperCase();
}

export function isRecent(ms: number, days = 7): boolean {
  return Date.now() - ms < days * 86_400_000;
}

// ── palette helpers ────────────────────────────────────────────────────────
export function hashIndex(seed: string, mod: number): number {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return Math.abs(h) % mod;
}

export function favVar(i: number): string {
  return `var(--fav-${((((i % 8) + 8) % 8) + 1)})`;
}

export const FAV_COLORS: readonly string[] = [
  'var(--fav-1)', 'var(--fav-2)', 'var(--fav-3)', 'var(--fav-4)',
  'var(--fav-5)', 'var(--fav-6)', 'var(--fav-7)', 'var(--fav-8)',
];

export function browserColor(b: Browser): string {
  if (b === 'chromium') return 'var(--br-chrome)';
  if (b === 'unknown') return 'var(--text-3)';
  return BROWSER_COLORS[b as BrowserName] ?? 'var(--text-3)';
}

export function browserLabel(b: Browser): string {
  if (b === 'unknown') return 'Browser';
  return b.charAt(0).toUpperCase() + b.slice(1);
}

// ── collection cover swatch ────────────────────────────────────────────────
export function Swatch({ seed, color, size = 15 }: { seed: string; color?: string | null; size?: number }) {
  const base = hashIndex(seed, 8);
  const cells = [0, 2, 4, 6].map((o) => favVar(base + o));
  if (color) cells[0] = color;
  const gap = Math.max(2, Math.round(size * 0.2));
  const radius = Math.max(3, Math.round(size * 0.27));
  return (
    <span style={{ display: 'grid', gridTemplateColumns: `${size}px ${size}px`, gap, flexShrink: 0 }}>
      {cells.map((c, i) => (
        <span key={i} style={{ width: size, height: size, borderRadius: radius, background: c }} />
      ))}
    </span>
  );
}

// ── color picker (create / rename) ─────────────────────────────────────────
export function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {FAV_COLORS.map((c) => {
        const selected = c === value;
        return (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            aria-label={selected ? 'Selected colour' : 'Pick colour'}
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: c,
              cursor: 'pointer',
              border: '2px solid transparent',
              outline: selected ? '2px solid var(--text-1)' : 'none',
              outlineOffset: 2,
            }}
          />
        );
      })}
    </div>
  );
}

// ── Pro upsell modal ───────────────────────────────────────────────────────
export function ProUpsellModal({
  open,
  onClose,
  title,
  body,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  body: string;
}) {
  const navigate = useNavigate();
  return (
    <Modal
      open={open}
      width={420}
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Not now</Button>
          <Button variant="primary" onClick={() => navigate('/pricing')}>Upgrade — $3/mo</Button>
        </>
      }
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: 11,
            background: 'var(--surface-2)',
            border: '1px solid var(--line-strong)',
            flexShrink: 0,
          }}
        >
          <Icon name="lock" size={16} color="var(--amber-ink)" />
        </span>
        <span style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.55 }}>{body}</span>
      </div>
    </Modal>
  );
}

// ── lightweight toast stack ────────────────────────────────────────────────
interface ToastMsg {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
}

export interface ToastApi {
  show: (t: { tone: ToastTone; title: string; description?: string }) => void;
  node: ReactNode;
}

export function useToasts(): ToastApi {
  const [items, setItems] = useState<ToastMsg[]>([]);

  const show = useCallback((t: { tone: ToastTone; title: string; description?: string }) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { id, ...t }]);
    window.setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), 3400);
  }, []);

  const node: ReactNode = (
    <div style={{ position: 'fixed', right: 20, bottom: 20, display: 'flex', flexDirection: 'column', gap: 10, zIndex: 60 }}>
      {items.map((t) => (
        <Toast key={t.id} inline tone={t.tone} title={t.title} description={t.description} />
      ))}
    </div>
  );

  return { show, node };
}

// ── responsive helper (available for JS-driven layout switches) ─────────────
export function useMediaQuery(query: string): boolean {
  const [match, setMatch] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = (): void => setMatch(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return match;
}
